/**
 * The merchant's catalogue, the master catalogue, and bulk import.
 *
 * All three are grouped here because the Inventory screen uses all three in one
 * flow: look at what you have, add from the shared list, or upload a file. It
 * splits when a second consumer appears — the POS will want products from an
 * offline table rather than this endpoint, and that is a different repository.
 */

import { api } from "@/api/client";

export interface Unit {
  id: number;
  code: string;
  name: string;
  plural_name: string;
  decimal_places: number;
}

export interface Barcode {
  id: string;
  barcode: string;
  is_primary: boolean;
}

/**
 * `cost_price` and `margin` are optional in the *type* because the server
 * removes them for anyone without `products.view_cost_price` — not blanks them,
 * removes them. The interface must therefore treat their absence as normal
 * rather than as an error, which is why these are `?` and not `| null`.
 */
export interface Product {
  id: string;
  name: string;
  sku: string | null;
  brand: string;
  pack_size: string;
  category: string | null;
  category_name?: string;
  category_colour?: string;
  unit: Unit;
  tax_rate: string;
  selling_price: string;
  cost_price?: string;
  margin?: string | null;
  search_keywords: string;
  /**
   * Already resolved by the server: the shop's own photograph if they took one,
   * otherwise the master catalogue's. Working that out on the client would be a
   * second implementation of the rule, and the two would drift.
   */
  image_url: string | null;
  image_thumb_url: string | null;
  /** Whether the picture is this shop's own. Drives the wording in the form. */
  has_own_image: boolean;
  tax?: TaxCategory | null;
  barcodes: Barcode[];
  shelf_location: string;
  reorder_level: string;
  is_active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface TaxCategory {
  id: string;
  code: string;
  name: string;
  treatment: string;
  rate: string;
  effective_rate: string;
  is_default: boolean;
}

export interface Category {
  id: string;
  name: string;
  colour: string;
  sort_order: number;
  product_count?: number;
}

/**
 * A shared-list record this shop already copied, and the product it became.
 *
 * Served by the *products* endpoint rather than the master catalogue one, which
 * would read more naturally: on the server `apps.catalogue` may not import
 * `apps.products`, so the master catalogue cannot answer a question about a
 * merchant's own shelves.
 */
export interface AdoptedMaster {
  master_id: string;
  product_id: string;
  name: string;
}

export interface MasterProduct {
  id: string;
  name: string;
  display_name: string;
  brand: string;
  pack_size: string;
  barcode: string | null;
  unit_code: string;
  category_code: string | null;
  category_name: string | null;
  search_keywords: string;
  is_zero_rated: boolean;
  indicative_price: string;
  image_url: string | null;
  image_thumb_url: string | null;
  /**
   * Sent with every picture rather than fetched on demand: the catalogue's
   * photographs are CC-BY-SA and the licence requires the credit to appear
   * wherever the image does.
   */
  image_attribution: string;
  image_licence: string;
}

export type ImportStatus =
  | "PENDING"
  | "VALIDATING"
  | "VALIDATED"
  | "IMPORTING"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED";

export type ImportRowStatus =
  "VALID" | "WARNING" | "ERROR" | "IMPORTED" | "SKIPPED" | "FAILED";

export interface ImportIssue {
  field: string;
  code: string;
  message: string;
  severity: "error" | "warning";
}

export interface ImportRow {
  id: string;
  row_number: number;
  status: ImportRowStatus;
  raw: Record<string, string>;
  data: Record<string, string>;
  issues: ImportIssue[];
  product: string | null;
}

export interface ImportJob {
  id: string;
  original_filename: string;
  file_format: string;
  status: ImportStatus;
  /** Also carries non-fatal notes: unmatched columns, a truncated file. */
  failure_reason: string;
  total_rows: number;
  valid_rows: number;
  warning_rows: number;
  error_rows: number;
  importable_rows: number;
  imported_rows: number;
  failed_rows: number;
  create_missing_categories: boolean;
  update_existing: boolean;
  created_by_name?: string;
  committed_at: string | null;
  created_at: string;
  problem_rows?: ImportRow[];
  preview_rows?: ImportRow[];
}

export interface ImportLimits {
  max_rows: number;
  max_bytes: number;
  formats: string[];
  columns: { key: string; heading: string; required: boolean; help: string }[];
}

interface Page<T> {
  count: number;
  results: T[];
}

async function all<T>(path: string): Promise<T[]> {
  const body = await api.get<Page<T> | T[]>(path);
  return Array.isArray(body) ? body : body.results;
}

export const catalogueRepository = {
  /** Attach a photograph. Multipart, so it does not go through `api.post`. */
  setImage: (productId: string, file: Blob) => {
    const form = new FormData();
    form.append("image", file, "photo.webp");
    return api.upload<Product>(`/products/${productId}/image/`, form);
  },

  /** Remove the shop's own photograph; the catalogue's takes over again. */
  clearImage: (productId: string) =>
    api.delete<Product>(`/products/${productId}/image/`),

  // ── The merchant's own catalogue ─────────────────────────────────────────
  products: (
    params: { q?: string | undefined; category?: string | undefined } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.q) query.set("q", params.q);
    if (params.category) query.set("category", params.category);
    // The search endpoint rather than the list endpoint even when the term is
    // empty: it applies the same ordering and the same section filtering, so
    // results do not reshuffle the moment someone starts typing.
    return api.get<Product[]>(`/products/search/?${query.toString()}`);
  },

  categories: () => all<Category>("/categories/"),

  units: () => all<Unit>("/units/"),

  taxCategories: () => all<TaxCategory>("/units/tax-categories/"),

  /**
   * Edit a product's details. Never its price — that goes through
   * `changePrice`, which records who changed it and why.
   */
  updateProduct: (
    id: string,
    patch: {
      name?: string;
      brand?: string;
      sku?: string | null;
      pack_size?: string;
      search_keywords?: string;
      shelf_location?: string;
      category_id?: string | null;
      tax_category_id?: string | null;
      unit_code?: string;
      track_stock?: boolean;
      reorder_level?: string;
      barcodes?: string[];
      expected_version?: number;
    },
  ) => api.patch<Product>(`/products/${id}/`, patch),

  changePrice: (
    id: string,
    input: { amount: string; price_type?: "SELLING" | "COST"; reason?: string },
  ) => api.post<{ amount: string }>(`/products/${id}/price/`, input),

  archiveProduct: (id: string) => api.delete<void>(`/products/${id}/`),

  // ── The master catalogue ─────────────────────────────────────────────────
  searchMaster: (q: string, category?: string) => {
    const query = new URLSearchParams({ q });
    if (category) query.set("category", category);
    return api.get<MasterProduct[]>(`/master-catalogue/?${query.toString()}`);
  },

  /**
   * The whole set, once — not a flag per search result. It is one row per
   * product a shop copied, it only changes when the shop adds something, and
   * fetching it up front means a search result can be marked the moment it
   * renders rather than after a second round trip behind every keystroke.
   */
  adoptedMasters: () => api.get<AdoptedMaster[]>("/products/adopted-masters/"),

  copyFromMaster: (input: {
    master_id: string;
    selling_price?: string;
    cost_price?: string;
    section_ids?: string[];
  }) => api.post<Product>("/products/copy-from-master/", input),

  // ── Bulk import ──────────────────────────────────────────────────────────
  importLimits: () => api.get<ImportLimits>("/imports/limits/"),

  imports: () => all<ImportJob>("/imports/"),

  importJob: (id: string) => api.get<ImportJob>(`/imports/${id}/`),

  /**
   * Upload and check. Creates nothing — the response is a verdict on every row,
   * and `commitImport` is the only call that writes products.
   */
  checkImport: (
    file: File,
    options: { createMissingCategories: boolean; updateExisting: boolean },
  ) => {
    const form = new FormData();
    form.append("file", file);
    // FormData carries strings, and Python's BooleanField reads "false" as
    // false — sending the JS boolean would stringify to "false" and work by
    // accident, which is a bad thing to rely on.
    form.append(
      "create_missing_categories",
      String(options.createMissingCategories),
    );
    form.append("update_existing", String(options.updateExisting));
    return api.upload<ImportJob>("/imports/", form);
  },

  commitImport: (id: string) => api.post<ImportJob>(`/imports/${id}/commit/`),

  cancelImport: (id: string) => api.post<ImportJob>(`/imports/${id}/cancel/`),

  importRows: (id: string, status?: ImportRowStatus) =>
    all<ImportRow>(`/imports/${id}/rows/${status ? `?status=${status}` : ""}`),

  downloadTemplate: () => api.download("/imports/template/"),

  downloadReport: (id: string) => api.download(`/imports/${id}/report/`),
};

/**
 * Hand a blob to the browser as a download.
 *
 * Lives here rather than in a component because it is the other half of
 * `downloadTemplate` and `downloadReport` — a caller that fetched a workbook and
 * then had to hand-roll an anchor element would inevitably forget to revoke the
 * object URL, which leaks the whole file until the tab closes.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// ─── Stock ────────────────────────────────────────────────────────────────────

export type StockStatus = "IN_STOCK" | "LOW_STOCK" | "OUT_OF_STOCK";

/** A catalogue product with its stock, as the Inventory table shows it. */
export interface StockProduct extends Product {
  stock_on_hand: string;
  /** Absent for anyone without `products.view_cost_price` — it discloses cost. */
  stock_value?: string;
  stock_status: StockStatus;
}

export interface StockMovement {
  id: string;
  product: string;
  product_name: string;
  movement_type: string;
  quantity: string;
  balance_after: string;
  unit_cost: string | null;
  reason: string;
  reference_type: string;
  occurred_at: string;
  business_date: string;
  by: string | null;
}

export interface SectionBalance {
  section_id: string;
  section_name: string;
  branch_name: string;
  quantity: string;
  average_cost: string;
}

export interface InventoryOverview {
  summary: {
    total_products: number;
    /** Omitted by the server when the caller may not see cost prices. */
    total_stock_value?: string;
    low_stock_items: number;
    out_of_stock_items: number;
  };
  status_breakdown: Record<string, number>;
  low_stock: {
    product_id: string;
    name: string;
    quantity: string;
    reorder_level: string;
    unit: string;
  }[];
  recent_movements: StockMovement[];
}

export const inventoryRepository = {
  /** One call for the four KPI cards, the doughnut and both side panels. */
  overview: (section?: string) =>
    api.get<InventoryOverview>(
      `/inventory/overview/${section ? `?section=${section}` : ""}`,
    ),

  products: (params: {
    q?: string;
    category?: string;
    status?: string;
    section?: string;
    page?: number;
  }) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value) query.set(key, String(value));
    }
    return api.get<Page<StockProduct>>(`/inventory/?${query.toString()}`);
  },

  /**
   * Where this product's stock actually sits, counter by counter.
   *
   * The table's `stock_on_hand` is a sum across every counter the caller can
   * see, so a form that changes one shelf must not preview against it.
   */
  balances: (productId: string) =>
    api.get<SectionBalance[]>(`/inventory/balances/?product=${productId}`),

  movements: (productId: string) =>
    api.get<Page<StockMovement>>(`/inventory/movements/?product=${productId}`),

  receive: (input: {
    product_id: string;
    section_id?: string;
    quantity: string;
    unit_cost?: string;
    reference?: string;
  }) => api.post<{ quantity_on_hand: string }>("/inventory/receive/", input),

  adjust: (input: {
    product_id: string;
    section_id?: string;
    quantity: string;
    reason: string;
    movement_type?: string;
  }) =>
    api.post<{ quantity_on_hand: string; went_negative: boolean }>(
      "/inventory/adjust/",
      input,
    ),

  count: (input: {
    product_id: string;
    section_id?: string;
    counted_quantity: string;
  }) =>
    api.post<{ quantity_on_hand?: string; detail?: string }>(
      "/inventory/count/",
      input,
    ),
};
